import { useState } from 'react';

import {
  Checkbox,
  Icon,
  TableCell,
  TableRow,
  TransparentIconButton,
} from '@procurify/ui'
import classnames from 'classnames'

import { useProcurifyIntl } from '@webapp/hooks'
import { type CommonImporterError } from './CommonImporterErrorStage'
import messages from '../../messages'

export interface IImporterErrorGroupProps {
  errorGroup: CommonImporterError[]
}

export const ImporterErrorGroup = ({
  errorGroup,
}: IImporterErrorGroupProps) => {
  const intl = useProcurifyIntl()

  const [group, setErrorGroup] =
    useState<CommonImporterError[]>(errorGroup)
  const [numberOfCheckedErrors, setNumberOfCheckedErrors] =
    useState<number>(0)

  const [isGroupCollapsed, setIsGroupCollapsed] = useState<boolean>(true)

  const updateSingleError = (errorKey: string) => {
    return group.map((error) => {
      if (error.errorKey === errorKey) error.isChecked = !error.isChecked
      return error
    })
  }

  const updateErrorGroup = () => {
    return group.map((error) => {
      error.isChecked = numberOfCheckedErrors !== group.length
      return error
    })
  }

  const onErrorCheck = (errorKey?: string) => {
    if (errorKey) {
      setErrorGroup(updateSingleError(errorKey))
    } else {
      setErrorGroup(updateErrorGroup())
    }
    setNumberOfCheckedErrors(group.filter((error) => error.isChecked).length)
  }

  const onErrorCollapsibleToggle = () => {
    setIsGroupCollapsed(!isGroupCollapsed)
  }

  return (
    <>
      {group.length > 1 && (
        <TableRow
          className={classnames({
            'row-checked': numberOfCheckedErrors === group.length,
          })}
        >
          <TableCell>
            <TransparentIconButton
              className='group-toggle-icon'
              aria-label='Import Error Group Toggle'
              onClick={onErrorCollapsibleToggle}
              icon={
                isGroupCollapsed ? 'chevron-thick-right' : 'chevron-thick-down'
              }
            />
          </TableCell>
          <TableCell>
            <Checkbox
              inputProps={{
                'aria-label': intl.formatMessage(
                  messages.errorGroupSelectCheckboxLabel
                ),
              }}
              checked={numberOfCheckedErrors > 0}
              indeterminate={
                numberOfCheckedErrors > 0 &&
                numberOfCheckedErrors < group.length
              }
              indeterminateIcon={<Icon icon='minus' bold />}
              onChange={() => onErrorCheck()}
            />
          </TableCell>
          <TableCell>
            {errorGroup.length}{' '}
            {intl.formatMessage(messages.errorListInstancesLabel)}
          </TableCell>
          <TableCell>{errorGroup[0].field}</TableCell>
          <TableCell />
          <TableCell>{errorGroup[0].message}</TableCell>
        </TableRow>
      )}

      {(group.length === 1 || !isGroupCollapsed) &&
        group.map((error) => (
          <TableRow
            key={error.errorKey}
            className={classnames({
              'row-checked': error.isChecked,
              'error-group-child-row': group.length > 1,
            })}
          >
            <TableCell />
            <TableCell>
              <Checkbox
                inputProps={{
                  'aria-label': intl.formatMessage(
                    messages.errorSelectCheckboxLabel,
                    {
                      csvLineIndex: error.csvLineIndex,
                      errorField: error.field,
                    }
                  ),
                }}
                checked={error.isChecked || false}
                onChange={() => onErrorCheck(error.errorKey)}
              />
            </TableCell>
            <TableCell>{error.csvLineIndex}</TableCell>
            <TableCell>{error.field}</TableCell>
            <TableCell>{error.value}</TableCell>
            <TableCell>{error.message}</TableCell>
          </TableRow>
        ))}
    </>
  )
}
